import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import dayjs from 'dayjs';
import {
  Button,
  Table,
  Avatar,
  Dropdown,
  Menu
} from 'antd';
import {
  notificationsAtom,
  payoutsHeaderAtom,
  viewPayoutAtom,
  viewLeadLogsAtom,
  userPrivilagesAtom,
  viewLeadAssignedAtom,
  editCommissionsAtom,
  reloadLeadsAtom
} from '../../../../../atoms/Atoms';
import api from '../../../../../api/api';
import { percentage, thousand, toCurrency, toFormattedLocalDate, toFormattedLocalTime } from '../../../../../functions';

function PayoutsSummaryTable({ orgCode, filter, useGlobal }) {
  const orgReferralCode = orgCode;
  const { t } = useTranslation();
  const setViewLogs = useSetRecoilState(viewLeadLogsAtom);
  const [reloadLeads, setReloadLeads] = useRecoilState(reloadLeadsAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [payoutHeader, setPayoutHeader] = useRecoilState(payoutsHeaderAtom);
  const setEditCommissions = useSetRecoilState(editCommissionsAtom);
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const setViewAssigned = useSetRecoilState(viewLeadAssignedAtom);
  const setViewPayout = useSetRecoilState(viewPayoutAtom);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  function search(page) {
    if (payoutHeader?.loading) {
      return;
    }
    if (!payoutHeader.start || !payoutHeader.end) {
      return;
    }
    setPayoutHeader({
      ...payoutHeader,
      loading: true
    });
    const startDate = dayjs(payoutHeader.start).add(4, 'hour');
    const endDate = dayjs(payoutHeader.end).add(4, 'hour');
    const startString = startDate.format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    api
      .post(
        'reports/payouts',
        {
          Filter: filter,
          OrgReferralCode: orgCode,
          OrgId: payoutHeader?.selectedOrgId,
          SetterId: payoutHeader?.selectedSetter,
          CloserId: payoutHeader?.selectedCloser,
          Sort: payoutHeader?.selectedSort,
          Page: page,
          Size: pageSize,
          StartDate: startString,
          EndDate: endString
        }
      )
      .then((response) => {
        setPayoutHeader({
          ...payoutHeader,
          loading: false
        });
        if (response?.data?.success) {
          setPayoutHeader({
            ...payoutHeader,
            leads: response.data.data.leads,
            totalLeads: response.data.data.total,
            installed: response.data.data.installed,
            scheduledPayments: response.data.data.scheduledPayments,
            scheduledInstalls: response.data.data.scheduledInstalls,
            paid: response.data.data.paid,
            closers: response.data.data.closers,
            setters: response.data.data.setters
          });
        } else {
            setPayoutHeader({
              ...payoutHeader,
              loading: false,
              leads: []
            });
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setPayoutHeader({
          ...payoutHeader,
          loading: false
        });
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  const onTableChange = (page, size) => {
    if (page - 1 !== currentPage) {
      setCurrentPage(page - 1);
      search(page - 1);
    } else {
      setPageSize(size);
    }
  };

  const tableColumns = [
    {
      title: '',
      align: 'left',
      className: 'text-sm w-full md:hidden',
      render: (row) => (
        <div className="grid grid-cols-2 gap-2 w-full">
          <div className="col-span-2 text-center">
            <>
              <p className="font-bold">{t('organization')}:</p>
              {row?.referredBy && (
                <div>
                  <span className="">{row?.referredBy?.nameEn}</span>
                </div>
              )}
                {!row?.referredBy && (
                <div className="italic text-gray-400">N/A</div>
              )}         
            </>
          </div>
          <div className="col-span-2 text-center">
            <p className="font-bold">{t('client')}:</p>
            <span>
              <Avatar src={row?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
              {row?.submittedBy?.completeName}
            </span>
          </div>
          <div className="col-span-2  text-center">
            <p className="font-bold">{t('deal_id')}:</p>
            <p>{row.externalDealId}</p>
          </div>
          <div className="col-span-2  text-center">
            <p className="font-bold">{t('closer')}:</p>
            <>
              {_.filter(row?.assignments, (a) => a.ownershipType === 0).length > 0 && (
                <div>
                  <p>
                    <Avatar src={_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                    {_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.completeName}
                  </p>
                  <p className="font-light">{t('max_active_deals')}: {_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.closerMaxActiveDeals}</p>
                </div>
              )}
              {_.filter(row?.assignments, (a) => a.ownershipType === 0).length === 0 && (
                <span>-</span>
              )}
            </>
          </div>
          <div className="col-span-2  text-center">
            <p className="font-bold">{t('setter')}:</p>
            <>
              {row?.setter && (
                <span>
                  <Avatar src={row?.setter?.avatar} className=" bg-primaryColor h12 w-8 mr-2" />
                  {row?.setter?.completeName}
                </span>
              )}
              {!row?.setter && (
                <span>{t('self-gen')}</span>
              )}
            </>
          </div>
          <div className="col-span-1">
            <p className="font-bold">{t('system_size')}:</p>
            <p>{thousand(row.systemSize)}</p>
          </div>
          <div className="col-span-1 text-right">
            <p className="font-bold">{t('deal_amount')}:</p>
            <p>{toCurrency(row.dealAmount)}</p>
          </div>
          <div className="col-span-1 text-left">
            <p className="font-bold">{t('closer_commision_amount')}:</p>
            <p>{toCurrency(row.effectiveCloserCommisionAmount)} ({percentage(row.closerCommisionPCT * 100, 2)})</p>
          </div>
          <div className="col-span-1 text-right">
            <p className="font-bold">{t('setter_commision_amount')}:</p>
            <p>{toCurrency(row.setterCommisionAmount)}</p>
          </div>

          <div className="col-span-2 text-center">
            <p className="font-bold">{filter === 0 ? t('scheduled_date') : (filter === 1 ? t('installed_date') : t('payout_date'))}</p>
            <>
              {filter === 0 && (
                <span>{row.signedDate ? toFormattedLocalDate(row.installationDateScheduled) : '-'}</span>
              )}
              {filter === 1 && (
                <span>{row.installationDate ? toFormattedLocalDate(row.installationDate) : '-'}</span>
              )}
              {filter === 2 && (
                <span>{row.payoutDate ? toFormattedLocalDate(row.payoutDate) : '-'}</span>
              )}
            </>
          </div>
          <div className="col-span-2 text-center">
            <Dropdown
              overlay={
                <Menu mode="inline" className="p-0">
                  <Menu.Item
                    id="view"
                    className="text-xs flex items-center"
                    onClick={() => {
                      setViewLogs(row);
                    }}
                  >
                    {t('notes')}
                  </Menu.Item>
                  <Menu.Item
                    id="viewassigned"
                    className="text-xs flex items-center"
                    onClick={() => {
                      setViewAssigned({
                        ...row,
                        referralCode: orgReferralCode
                      });
                    }}
                  >
                    {t('view_assigned')}
                  </Menu.Item>
                  {userPrivilages.hasCloserAdminPrivilages && (
                    <>
                      <Menu.Item
                        id="commissions"
                        className="text-xs flex items-center"
                        onClick={() => setEditCommissions(row)}
                      >
                        {t('commissions')}
                      </Menu.Item>
                      <Menu.Item
                        id="payout"
                        className="text-xs flex items-center"
                        onClick={() => setViewPayout(row)}
                      >
                        {t('payout')}
                      </Menu.Item>
                    </>
                  )}
                </Menu>
              }
            >
              <Button size="small" className="w-full" type="primary">{t('actions')}</Button>
            </Dropdown>
          </div>
        </div>
      ),
    },
    {
      title: t('client'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>
          <Avatar src={row?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
          {row?.submittedBy?.completeName}
        </span>
      ),
    },
    {
      title: t('deal_id'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <p>{row.externalDealId}</p>
      ),
    },
    {
      title: t('closer'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <>
          {_.filter(row?.assignments, (a) => a.ownershipType === 0).length > 0 && (
            <div>
              <p>
                {/* <Avatar src={_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.avatar} className="bg-primaryColor h12 w-8 mr-2" /> */}
                {_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.completeName}
              </p>
              <p className="font-light">{t('max_active_deals')}: {_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.closerMaxActiveDeals}</p>
            </div>
          )}
          {_.filter(row?.assignments, (a) => a.ownershipType === 0).length === 0 && (
            <span>-</span>
          )}
        </>
      ),
    },
    {
      title: t('setter'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <>
          {row?.setter && (
            <span>
              <Avatar src={row?.setter?.avatar} className=" bg-primaryColor h12 w-8 mr-2" />
              {row?.setter?.completeName}
            </span>
          )}
          {!row?.setter && (
            <span>{t('self-gen')}</span>
          )}
        </>
      ),
    },
    {
      title: t('system_size'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <p>{thousand(row.systemSize)}</p>
      ),
    },
    {
      title: t('deal_amount'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <p>{toCurrency(row.dealAmount)}</p>
      ),
    },
    {
      title: t('closer_commision_amount'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <p>{toCurrency(row.effectiveCloserCommisionAmount)} ({percentage(row.closerCommisionPCT * 100, 2)})</p>
      ),
    },
    {
      title: t('setter_commision_amount'),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <p>{toCurrency(row.setterCommisionAmount)}</p>
      ),
    },
    {
      title: filter === 0 ? t('scheduled_date') : (filter === 1 ? t('installed_date') : t('payout_date')),
      align: 'left',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <>
          {filter === 0 && (
            <span>{row.signedDate ? toFormattedLocalDate(row.installationDateScheduled) : '-'}</span>
          )}
          {filter === 1 && (
            <span>{row.installationDate ? toFormattedLocalDate(row.installationDate) : '-'}</span>
          )}
          {filter === 2 && (
            <span>{row.payoutDate ? toFormattedLocalDate(row.payoutDate) : '-'}</span>
          )}
        </>
      ),
    },
    {
      title: t('actions'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <Dropdown
          overlay={
            <Menu mode="inline" className="p-0">
              <Menu.Item
                id="view"
                className="text-xs flex items-center"
                onClick={() => {
                  setViewLogs(row);
                }}
              >
                {t('notes')}
              </Menu.Item>
              <Menu.Item
                id="viewassigned"
                className="text-xs flex items-center"
                onClick={() => {
                  setViewAssigned({
                    ...row,
                    referralCode: orgReferralCode
                  });
                }}
              >
                {t('view_assigned')}
              </Menu.Item>
              {userPrivilages.hasCloserAdminPrivilages && (
                <>
                  <Menu.Item
                    id="commissions"
                    className="text-xs flex items-center"
                    onClick={() => setEditCommissions(row)}
                  >
                    {t('commissions')}
                  </Menu.Item>
                  <Menu.Item
                    id="payout"
                    className="text-xs flex items-center"
                    onClick={() => setViewPayout(row)}
                  >
                    {t('payout')}
                  </Menu.Item>
                </>
              )}
            </Menu>
          }
        >
          <Button size="small" type="primary">{t('actions')}</Button>
        </Dropdown>
      ),
    },
  ];

  useEffect(() => {
    if (reloadLeads) {
      setReloadLeads(false);
      search(currentPage);
    }
  }, [reloadLeads]);

  useEffect(() => {
    search(0);
  }, [pageSize, payoutHeader?.start, payoutHeader?.end, payoutHeader?.selectedCloser, payoutHeader?.selectedSetter, payoutHeader?.selectedSort, payoutHeader?.selectedOrgId]);

  useEffect(() => {
    setPayoutHeader({
      ...payoutHeader,
      leads: [],
      closers: [],
      setters: []
    });
  }, []);

  return (
    <div>
      <div className="flex">
        <Button
          className="ml-auto mb-2"
          type="primary"
          disabled={payoutHeader?.loading}
          onClick={() => {
            search(currentPage);
          }}
        >
          {t('reload')}
        </Button>
      </div>
      <Table
        rowKey="id"
        locale={{
          emptyText: t('no_data')
        }}
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage + 1,
          total: payoutHeader?.totalLeads,
          onChange: onTableChange
        }}
        bordered
        loading={payoutHeader?.loading}
        dataSource={payoutHeader?.leads}
        columns={useGlobal ? [
          {
            title: t('organization'),
            align: 'left',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <>
                {row?.referredBy && (
                  <div>
                    <span className="">{row?.referredBy?.nameEn}</span>
                  </div>
                )}
                  {!row?.referredBy && (
                  <div className="italic text-gray-400">N/A</div>
                )}         
              </>
            ),
          },
          ...tableColumns
        ] : tableColumns}
      />
    </div>
  );
}

export default PayoutsSummaryTable;
